<script setup lang="ts">
import { computed, ComputedRef, ConcreteComponent, defineProps } from 'vue'
import { AccountNavigationStore, PersonalAccountPage } from '../../utilities/account/useAccountNavigationPersonal'

import { PersonalAccountData } from '../../utilities/account/useAccountDataPersonal'
import { CheckIcon } from '@heroicons/vue/solid'
import { InformationCircleIcon } from '@heroicons/vue/outline'
import RequiredFieldsNotice from './RequiredFieldsNotice.vue'
import { PersonalAccountFormStore } from '../../utilities/account/useAccountFormPersonal'
import { LegalAccountNavigationStore, LegalAccountPage } from '../../utilities/account/useAccountNavigationLegal'
import { LegalAccountFormStore } from '../../utilities/account/useAccountFormLegal'
import { LegalAccountData } from '../../utilities/account/useAccountDataLegal'
import { NavigationItemParent } from '../../typings'
import AccountPanelIcon from './AccountPanelIcon.vue'
import { handleSimplyTokenizedRedirects } from '../../utilities/account/useCryptoWallet'
import BaseButtonSecondary from '../base/BaseButtonSecondary.vue'

const props = defineProps<{
    navigationStore: AccountNavigationStore | LegalAccountNavigationStore
    store: PersonalAccountData | LegalAccountData
    accountFormStore: PersonalAccountFormStore | LegalAccountFormStore
    navigationItems: NavigationItemParent[]
    router: { [key: PersonalAccountPage | LegalAccountPage]: RouterItem }
}>()

export interface RouterItem {
    title: string
    subtitle?: string
    component: ConcreteComponent
    hideFooter?: ComputedRef<boolean>
    hideHeader?: ComputedRef<boolean>
    customPanel?: ComputedRef<boolean>
}

const currentRouterItem = computed<RouterItem>(() => props.router[props.navigationStore.page])

handleSimplyTokenizedRedirects()
</script>

<template>
    <div v-if="currentRouterItem?.customPanel?.value">
        <div class="grid w-full grid-cols-1 space-y-3" :class="{ 'pb-4': $slots.alert }">
            <slot name="alerts" />
        </div>
        <div>
            <Component :is="currentRouterItem.component" />
        </div>
    </div>
    <div v-else>
        <div class="grid w-full grid-cols-1 space-y-3" :class="{ 'pb-4': $slots.alert }">
            <slot name="alerts" />
        </div>

        <div class="rounded-lg border border-gray-200 bg-white shadow-md">
            <div
                v-if="!currentRouterItem?.hideHeader?.value"
                class="flex flex-wrap items-center justify-between px-4 py-5 sm:px-5">
                <div class="flex items-center space-x-3">
                    <div class="inline-block rounded-full bg-gray-100 p-2">
                        <AccountPanelIcon
                            class="w-6"
                            :page="navigationStore.page"
                            :navigation-items="navigationItems" />
                    </div>
                    <h3 class="text-xl font-semibold leading-6 text-gray-700">{{ currentRouterItem.title }}</h3>
                </div>
                <div class="ml-[3.3rem]">
                    <slot name="header-right" />
                </div>
            </div>
            <div
                v-if="!currentRouterItem?.hideHeader?.value && currentRouterItem.subtitle"
                class="flex items-start space-x-4 border-t border-gray-200 bg-gray-50 px-4 py-5 sm:px-6">
                <InformationCircleIcon class="w-7 flex-none"></InformationCircleIcon>
                <p class="self-center text-sm text-gray-600" v-html="currentRouterItem.subtitle"></p>
            </div>

            <div v-if="!store.loading" class="border-t border-gray-200">
                <form @submit.prevent>
                    <Component :is="currentRouterItem.component" />
                    <div
                        v-if="!currentRouterItem?.hideFooter?.value"
                        class="mt-3 justify-between space-y-4 border-t border-gray-300 px-4 py-5 pt-6 sm:flex sm:space-y-0 sm:px-6">
                        <RequiredFieldsNotice />
                        <BaseButtonSecondary
                            :icon="CheckIcon"
                            size="lg"
                            :loading="store.saving"
                            :disabled="store.saving || accountFormStore.hasValidationErrors"
                            @click="accountFormStore.submit">
                            {{ $t('account.save') }}
                        </BaseButtonSecondary>
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>
